import * as React from 'react'

import { Avatar as AvatarPrimitive } from 'radix-ui'

import { cn } from './cn'

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  src?: string
  alt?: string
  variant?: 'circular' | 'rounded' | 'square'
}

const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ src, alt, variant = 'circular', className, children, ...props }, ref) => (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex h-10 w-10 shrink-0 select-none items-center justify-center overflow-hidden',
        'bg-gray-400 text-xl text-white dark:bg-gray-600',
        variant === 'circular' && 'rounded-full',
        variant === 'rounded' && 'rounded',
        className,
      )}
      {...props}
    >
      {src && (
        <AvatarPrimitive.Image
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
        />
      )}
      <AvatarPrimitive.Fallback className="flex h-full w-full items-center justify-center">
        {children ?? (alt ? alt.charAt(0).toUpperCase() : null)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  ),
)
Avatar.displayName = 'Avatar'

export default Avatar

export const AvatarGroup = ({
  max,
  className,
  children,
}: {
  max?: number
  className?: string
  children: React.ReactNode
}) => {
  const items = React.Children.toArray(children)
  const shown = max ? items.slice(0, max) : items
  const surplus = items.length - shown.length
  return (
    <div className={cn('flex flex-row-reverse justify-end', className)}>
      {surplus > 0 && (
        <span className="relative -ml-2 flex h-10 w-10 items-center justify-center rounded-full border-2 border-paper bg-gray-300 text-sm text-gray-700 first:ml-0 dark:bg-gray-700 dark:text-gray-200">
          +{surplus}
        </span>
      )}
      {shown.reverse().map((child, i) => (
        <span
          key={i}
          className="relative -ml-2 rounded-full border-2 border-paper first:ml-0 [&>*]:h-10 [&>*]:w-10"
        >
          {child}
        </span>
      ))}
    </div>
  )
}
